import React, { useRef, useState } from 'react';
import { Film, Upload, Trash2 } from 'lucide-react';
import { imageAPI, imageUtils } from '../services/imageApi';
import { isManagedMediaUrl } from '../utils/mediaImageUtils';

/**
 * Upload a Paw Stories reel to S3 (admin/vendor) or paste a URL.
 * @param {string} videoUrl
 * @param {(url: string) => void} onVideoUrlChange
 * @param {'admin' | 'vendor'} variant
 */
export default function PawStoryVideoUpload({ videoUrl, onVideoUrlChange, variant = 'admin' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isVendor = variant === 'vendor';

  const border = isVendor ? 'border-gray-300' : 'border-[#E5E3DF]';
  const focus = isVendor
    ? 'focus:ring-2 focus:ring-gray-300 focus:border-gray-400'
    : 'focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]';
  const btnPrimary = isVendor
    ? 'bg-gray-900 text-white hover:bg-gray-800'
    : 'bg-[#FF5E4C] text-white hover:opacity-90';

  const handlePickFile = () => inputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!imageUtils.isValidPawStoryVideoType(file)) {
      alert('Please choose MP4, MOV, or WebM.');
      return;
    }
    if (!imageUtils.isValidPawStoryVideoSize(file)) {
      alert('Video must be 500MB or smaller (raw upload). Larger files are not accepted.');
      return;
    }

    setUploading(true);
    try {
      const response = await imageAPI.uploadPawStoryVideo(file, 'paw-stories');
      const url = response.uploaded?.[0]?.url;
      if (!url) throw new Error('No URL returned from upload');
      onVideoUrlChange(url);
    } catch (error) {
      console.error('Paw story video upload:', error);
      const details =
        error.response?.data?.details || error.response?.data?.error || error.message;
      alert(details ? `Upload failed: ${details}` : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    const prev = (videoUrl || '').trim();
    if (!prev) return;
    if (window.confirm('Remove this reel from the listing?')) {
      if (isManagedMediaUrl(prev)) {
        try {
          await imageAPI.deleteImage(prev);
        } catch (e) {
          console.warn('Could not delete video from storage (you can still clear the link):', e);
        }
      }
      onVideoUrlChange('');
    }
  };

  return (
    <div className="space-y-4">
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v"
        className="hidden"
        onChange={handleFileChange}
        disabled={uploading}
      />

      <div className={`flex flex-wrap items-center gap-3 ${isVendor ? '' : ''}`}>
        <button
          type="button"
          onClick={handlePickFile}
          disabled={uploading}
          className={`inline-flex items-center gap-2 rounded-[12px] px-4 py-2.5 text-sm font-medium transition-opacity disabled:opacity-50 ${btnPrimary}`}
        >
          <Upload className="w-4 h-4 shrink-0" strokeWidth={2} />
          {uploading ? 'Compressing & uploading…' : 'Upload reel'}
        </button>
        {videoUrl ? (
          <button
            type="button"
            onClick={handleRemove}
            disabled={uploading}
            className={`inline-flex items-center gap-2 rounded-[12px] border px-4 py-2.5 text-sm font-medium text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50 ${border}`}
          >
            <Trash2 className="w-4 h-4 shrink-0" strokeWidth={2} />
            Remove
          </button>
        ) : null}
      </div>

      <div>
        <label
          className={`block text-sm font-medium mb-2 ${isVendor ? 'text-gray-700' : 'text-[#5c5c5c]'}`}
        >
          Or paste video URL
        </label>
        <div className="flex gap-2 items-start">
          <Film className={`w-5 h-5 shrink-0 mt-2.5 ${isVendor ? 'text-gray-400' : 'text-[#78736A]'}`} />
          <input
            type="text"
            value={videoUrl || ''}
            onChange={(e) => onVideoUrlChange(e.target.value)}
            disabled={uploading}
            className={`flex-1 min-w-0 px-3 py-2 border rounded-[12px] text-sm ${border} ${focus} disabled:bg-gray-50`}
            placeholder="https://… or /reels/your-file.mp4"
          />
        </div>
      </div>

      <p className={`text-xs ${isVendor ? 'text-gray-500' : 'text-[#78736A]'}`}>
        MP4, MOV, or WebM up to 500MB. The server compresses reels so the delivered file is at most about 10MB—large uploads may take a few minutes.
      </p>

      {videoUrl ? (
        <p className={`text-xs break-all ${isVendor ? 'text-gray-500' : 'text-[#78736A]'}`}>
          Current: {videoUrl}
        </p>
      ) : null}
    </div>
  );
}
